import { proxy } from 'valtio';
import { isAddress } from 'viem/utils';
import { z } from 'zod';
import { CreditRoleEnum } from '@soundxyz/utils/const';
import { MAX_OWNERS } from '@utils/constants';
import { initialSplitsErrors, SplitsErrors } from './errors';

const numericFloat = z.union([z.string(), z.number()]).transform(v => {
  const stringNum = v.toString();
  return stringNum ? parseFloat(stringNum) : null;
});

export const Split = z.object({
  ownerAddress: z.string().transform(v => v.toLowerCase()),
  percent: numericFloat.transform(v => v ?? 0),
  roles: z.array(z.nativeEnum(CreditRoleEnum)),
});

export const SplitFieldOptions = Split.keyof().options;
export type SplitFieldOptionsType = (typeof SplitFieldOptions)[number];

const addSplitsIssue = (message: string, ctx: z.RefinementCtx) => {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message,
    path: ['splits'],
  });
};

export const ValidatableFields = z
  .object({
    splits: z.array(Split).transform(v => v ?? []),
  })
  .superRefine(({ splits }, ctx) => {
    const addresses = splits.map(split => split.ownerAddress);
    const percents = splits.map(split => split.percent);
    const roles = splits.map(split => split.roles);

    if (splits.length >= MAX_OWNERS) {
      addSplitsIssue(
        `You can only define a maximum of ${MAX_OWNERS} owners per split at this time`,
        ctx,
      );
    }

    // check for missing addresses
    for (const address of addresses) {
      if (!address) {
        addSplitsIssue('Please fill all address fields', ctx);
        return;
      }
    }
    // check for duplicates
    const addressSet = new Set(addresses.map(address => address.toLowerCase()));
    if (addressSet.size !== addresses.length) {
      addSplitsIssue('Please remove duplicate addresses', ctx);
      return;
    }

    // check for 0 or negative amounts
    for (const percent of percents) {
      if (!+percent) {
        addSplitsIssue('Please provide a percentage for every owner', ctx);
        return;
      }
      if (+percent < 0) {
        addSplitsIssue('No negative percentages allowed', ctx);
        return;
      }
    }

    // combine data
    const total =
      percents.map(percent => +percent * 100).reduce((prev, curr) => prev + curr, 0) / 100;
    const invalidAddresses = addresses.filter(address => !isAddress(address));

    if (invalidAddresses.length) {
      addSplitsIssue(`Invalid address: ${invalidAddresses[0]}`, ctx);
      return;
    } else if (total !== 100) {
      addSplitsIssue('Total must equal 100%', ctx);
      return;
    }

    // check for missing roles
    for (const userRoles of roles) {
      if (!userRoles || userRoles.length === 0) {
        addSplitsIssue('Please provide a role for each owner', ctx);
        return;
      }
    }
  });

export const SplitsFields = ValidatableFields;

export type SplitsFieldsKeys = keyof z.infer<typeof SplitsFields>;

type SplitsFields = (typeof SplitsFields)['_input'];

export const initialSplitsFields = () =>
  ({
    splits: [],
  }) satisfies SplitsFields;

export const initializeSplitsFieldsWithOwnerAddress = (authUserAddress: string) => {
  splitsState.fields.splits = [
    { ownerAddress: authUserAddress, roles: [CreditRoleEnum.ARTIST], percent: '100' },
  ];
};

export const resetSplitsState = () => {
  splitsState.fields = initialSplitsFields();
  splitsState.errors = initialSplitsErrors();
  splitsState.isValid = false;
  splitsState.touched = initialSplitsTouchedFields();
};

export const SplitsTouchedFields = z.object({
  splits: z.boolean(),
});

type SplitsTouchedFields = (typeof SplitsTouchedFields)['_input'];

export const initialSplitsTouchedFields = () =>
  ({
    splits: false,
  }) satisfies SplitsTouchedFields;

export const SplitsSchema = z.object({
  fields: SplitsFields,
  touched: SplitsTouchedFields,
  errors: SplitsErrors,
  isValid: z.boolean(),
});

export type SplitsState = (typeof SplitsSchema)['_input'];

export const splitsState: SplitsState = proxy({
  errors: initialSplitsErrors(),
  fields: initialSplitsFields(),
  touched: initialSplitsTouchedFields(),
  isValid: false,
});
